import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import appMessage from '../../../utils/appMessage';

const VALID_TOAST_KINDS = ['error', 'success', 'warning', 'info', 'long-error'] as const;
type DevToastKind = (typeof VALID_TOAST_KINDS)[number];

function isDevToastKind(value: string | null): value is DevToastKind {
  return value !== null && (VALID_TOAST_KINDS as readonly string[]).includes(value);
}

/**
 * Gatilhos de QA em desenvolvimento (query string):
 * - ?forceError=render → Error Boundary
 * - ?forceToast=error|success|warning|info|long-error → toasts v2
 * - /register?devStep=3 → etapa comercial do cadastro (ver registerDevBypass.ts)
 */
const DevQaTriggers: React.FC = () => {
  const { search } = useLocation();
  const lastToastKeyRef = useRef<string | null>(null);

  if (process.env.NODE_ENV === 'development') {
    const params = new URLSearchParams(search);
    if (params.get('forceError') === 'render') {
      throw new Error('DevQaTriggers: erro de render forçado (?forceError=render)');
    }
  }

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;

    const kind = new URLSearchParams(search).get('forceToast');
    if (!isDevToastKind(kind)) return;

    const fireKey = `${search}:${kind}`;
    if (lastToastKeyRef.current === fireKey) return;
    lastToastKeyRef.current = fireKey;

    switch (kind) {
      case 'error':
        appMessage.showError(new Error('Toast de erro (dev) — algo falhou na ação.'));
        break;
      case 'success':
        appMessage.showSuccess('Toast de sucesso (dev) — ação concluída.');
        break;
      case 'warning':
        appMessage.showWarning('Toast de aviso (dev) — verifique antes de continuar.');
        break;
      case 'info':
        appMessage.showInfo('Toast informativo (dev) — atualização disponível.');
        break;
      case 'long-error':
        appMessage.showError(
          new Error(
            'Toast de erro longo (dev): mensagem de validação extensa para testar duração manual e layout do conteúdo no canto superior da tela, sem cortar o texto importante para o usuário.'
          )
        );
        break;
      default:
        break;
    }
  }, [search]);

  return null;
};

export default DevQaTriggers;
